import { LatLon } from '../domain/types.ts';

export interface OverpassElement {
  type: 'node' | 'way';
  id: number;
  lat?: number;
  lon?: number;
  nodes?: number[];
  tags?: Record<string, string>;
  geometry?: Array<{ lat: number; lon: number }>;
}

export interface OverpassResponse {
  elements: OverpassElement[];
}

export class OsmClient {
  private static readonly WORKER_OSM_ENDPOINT = 'https://tradaviahe.tradaviahe.workers.dev/api/osm';

  // Danh sách các public Overpass endpoints dự phòng (Failover mirrors)
  private static readonly ENDPOINTS = [
    'https://overpass-api.de/api/interpreter',
    'https://lz4.overpass-api.de/api/interpreter',
    'https://overpass.kumi.systems/api/interpreter',
    'https://overpass.osm.ch/api/interpreter',
  ];

  public static async fetchNeighborhoodData(
    center: LatLon,
    radiusM = 500,
    timeoutMs = 7000
  ): Promise<OverpassResponse | null> {
    const query = `[out:json][timeout:15];(way["highway"~"primary|secondary|tertiary|residential|pedestrian|living_street|unclassified"](around:${radiusM},${center.lat},${center.lon}););out geom 80;(node["amenity"~"school|hospital|marketplace|cafe|restaurant|fast_food"](around:${radiusM},${center.lat},${center.lon});node["shop"~"convenience|kiosk"](around:${radiusM},${center.lat},${center.lon}););out 30;`;

    // Thực hiện 2 vòng thử nghiệm (2 attempts)
    for (let attempt = 1; attempt <= 2; attempt++) {
      console.log(`[OsmClient] Attempt ${attempt}/2 fetching live OSM data...`);

      // 1. Thử gọi Cloudflare Edge Proxy trước (nhanh, có cache KV, không bị chặn User-Agent/CORS)
      try {
        const workerUrl = `${this.WORKER_OSM_ENDPOINT}?lat=${center.lat}&lon=${center.lon}&radius=${radiusM}`;
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 12000); // 12 giây

        const res = await fetch(workerUrl, { signal: controller.signal });
        clearTimeout(timer);

        if (res.ok) {
          const data = (await res.json()) as OverpassResponse;
          if (data && Array.isArray(data.elements) && data.elements.length > 0) {
            const wayCount = data.elements.filter((e) => e.type === 'way').length;
            if (wayCount >= 2) {
              console.log(`[OsmClient] Live OSM data fetched via Cloudflare Edge (${wayCount} ways, ${data.elements.length} total)`);
              return data;
            }
          }
        }
      } catch {
        // Tiếp tục fallback sang mirrors công cộng
      }

      // 2. Thử gọi trực tiếp OpenStreetMap Official REST API (Không bị Overpass queue/timeout)
      try {
        const officialData = await this.fetchFromOfficialOsmApi(center, radiusM);
        if (officialData) {
          return officialData;
        }
      } catch {
        // Tiếp tục fallback sang Overpass mirrors
      }

      // 3. Fallback: Thử lần lượt các public mirrors sử dụng GET query
      for (const endpoint of this.ENDPOINTS) {
        try {
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), timeoutMs);

          const targetUrl = `${endpoint}?data=${encodeURIComponent(query)}`;
          const response = await fetch(targetUrl, {
            method: 'GET',
            signal: controller.signal,
          });

          clearTimeout(timer);

          if (!response.ok) {
            continue;
          }

          const data = (await response.json()) as OverpassResponse;
          if (data && Array.isArray(data.elements) && data.elements.length > 0) {
            const wayCount = data.elements.filter((e) => e.type === 'way').length;
            if (wayCount >= 2) {
              console.log(`[OsmClient] Live OSM data fetched from ${endpoint} (${wayCount} ways, ${data.elements.length} total)`);
              return data;
            }
          }
        } catch {
          // Thử endpoint tiếp theo
        }
      }

      if (attempt < 2) {
        // Chờ 600ms trước khi retry vòng 2
        await new Promise((r) => setTimeout(r, 600));
      }
    }

    console.warn('[OsmClient] All live OSM mirrors failed after 2 attempts.');
    return null;
  }

  /**
   * Gọi trực tiếp OpenStreetMap 0.6 Map Data API (Official OSM Foundation API)
   * Không phụ thuộc Overpass indexing queue, trả về đầy đủ roads và POIs trong bbox 500m
   */
  private static async fetchFromOfficialOsmApi(center: LatLon, radiusM = 500): Promise<OverpassResponse | null> {
    try {
      const radiusDeg = radiusM / 111320;
      const minLat = center.lat - radiusDeg;
      const maxLat = center.lat + radiusDeg;
      const cosLat = Math.cos((center.lat * Math.PI) / 180);
      const lonRadiusDeg = cosLat > 0.01 ? radiusDeg / cosLat : radiusDeg;
      const minLon = center.lon - lonRadiusDeg;
      const maxLon = center.lon + lonRadiusDeg;

      const url = `https://api.openstreetmap.org/api/0.6/map.json?bbox=${minLon.toFixed(5)},${minLat.toFixed(5)},${maxLon.toFixed(5)},${maxLat.toFixed(5)}`;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 7000);

      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          'User-Agent': 'BaChuViaHe/2.0 (Arcade Tycoon Game; contact: dev@bachuviahe.com)',
          'Accept': 'application/json',
        },
      });
      clearTimeout(timer);

      if (!response.ok) return null;
      const data = (await response.json()) as { elements?: Array<OverpassElement & { nodes?: number[] }> };
      if (!data || !Array.isArray(data.elements) || data.elements.length === 0) return null;

      // Xây dựng bảng tra cứu tọa độ từ các node
      const nodeMap = new Map<number, { lat: number; lon: number }>();
      for (const el of data.elements) {
        if (el.type === 'node' && typeof el.lat === 'number' && typeof el.lon === 'number') {
          nodeMap.set(el.id, { lat: el.lat, lon: el.lon });
        }
      }

      // Gán geometry đầy đủ cho các way
      const enrichedElements: OverpassElement[] = [];
      let wayCount = 0;

      for (const el of data.elements) {
        if (el.type === 'way' && el.tags && el.tags.highway) {
          const geometry: Array<{ lat: number; lon: number }> = [];
          if (Array.isArray(el.nodes)) {
            for (const nid of el.nodes) {
              const pt = nodeMap.get(nid);
              if (pt) geometry.push(pt);
            }
          }
          if (geometry.length >= 2) {
            enrichedElements.push({
              ...el,
              geometry,
            });
            wayCount++;
          }
        } else if (el.type === 'node' && el.tags && (el.tags.amenity || el.tags.shop)) {
          enrichedElements.push(el);
        }
      }

      if (wayCount >= 2) {
        console.log(`[OsmClient] Live OSM data fetched via Official OSM 0.6 API (${wayCount} ways, ${enrichedElements.length} total)`);
        return { elements: enrichedElements };
      }
      return null;
    } catch {
      return null;
    }
  }
}
